import { useOnceEffect } from "@core/hooks/use-once-effect";
import { useNavigate } from "@core/routing/use-navigate";
import { analyticsService, userStatusManager } from "@core/service-container";
import { AnalyticsCategory } from "@domain/analytics/analytics-types";
import { useUser } from "@domain/user/hooks/use-user";
import { IdentityStatus, RegistrationStatus } from "@domain/user/user";
import styled from "@emotion/styled";
import { RoutesPath } from "@ui/navigation/routes";
import { RegisterContainer } from "@ui/pages/validate-professional-information/components/register-container";
import RegisterDescriptionProfInfo from "@ui/pages/validate-professional-information/components/register-description-prof-info";
import {
	ProfessionalInformationValidationSteps,
	RegisterProgressionBar,
} from "@ui/pages/validate-professional-information/components/register-progression-bar";
import { IntroductionStep } from "@ui/pages/validate-professional-information/steps/introduction-step";
import { ProfessionalInfoStep } from "@ui/pages/validate-professional-information/steps/professional-info-step";
import { RegisterCompletedStep } from "@ui/pages/validate-professional-information/steps/register-completed-step";
import { RegisterOnfidoIdentityVerificationStep } from "@ui/pages/validate-professional-information/steps/register-onfido-identity-verification-step";
import { stack } from "@ui/style";
import { colors } from "@ui/style/colors";
import { useObservable } from "micro-observables";
import React, { useCallback, useEffect, useState } from "react";

export const RegisterProfessionalInformationPage: React.FC = () => {
	const navigate = useNavigate();
	const [currentStep, setCurrentStep] = useState<ProfessionalInformationValidationSteps>();
	const hcpUserStatus = useObservable(userStatusManager.hcpStatus);
	const ptRegistrationStatus = useObservable(userStatusManager.ptRegistrationStatus);
	const identityStatus = useObservable(userStatusManager.identityStatus);
	const user = useUser();
	const currentPtId = user?.id ? user.id : "";

	const isScenario2 = hcpUserStatus === RegistrationStatus.INCOMPLETE && identityStatus === IdentityStatus.NOT_NEEDED;
	const isScenario3 = hcpUserStatus === RegistrationStatus.NOT_NEEDED && identityStatus === IdentityStatus.INCOMPLETE;

	const statusesLoaded = ptRegistrationStatus !== null && hcpUserStatus !== null && identityStatus !== null;

	useOnceEffect(() => {
		if (!userStatusManager.hasPtViewedIntroduction(currentPtId) && !isScenario2 && !isScenario3) {
			setCurrentStep(ProfessionalInformationValidationSteps.INTRODUCTION);
		} else if (
			ptRegistrationStatus === RegistrationStatus.INCOMPLETE &&
			hcpUserStatus === RegistrationStatus.INCOMPLETE
		) {
			setCurrentStep(ProfessionalInformationValidationSteps.PROFESSIONAL_INFORMATION);
		} else if (hcpUserStatus === RegistrationStatus.PENDING) {
			setCurrentStep(ProfessionalInformationValidationSteps.IDENTITY_VERIFICATION);
		}
	}, statusesLoaded);

	useEffect(() => {
		if (
			hcpUserStatus === RegistrationStatus.NOT_NEEDED &&
			identityStatus === IdentityStatus.INCOMPLETE
		) {
			setCurrentStep(ProfessionalInformationValidationSteps.IDENTITY_VERIFICATION);
		}
	}, [hcpUserStatus, identityStatus]);

	const renderStep = useCallback(
		(step: ProfessionalInformationValidationSteps | undefined) => {
			switch (step) {
				case ProfessionalInformationValidationSteps.INTRODUCTION:
					analyticsService.trackEvent({
						category: AnalyticsCategory.REGISTRATION,
						action: "reachStep",
						params: ["INTRODUCTION"],
					});

					return (
						<IntroductionStep
							onNext={() => {
								userStatusManager.updatePtViewedIntroduction(currentPtId);
								setCurrentStep(
									userStatusManager.hcpStatus.get() === RegistrationStatus.PENDING
										? ProfessionalInformationValidationSteps.IDENTITY_VERIFICATION
										: ProfessionalInformationValidationSteps.PROFESSIONAL_INFORMATION,
								);
							}}
						/>
					);
				case ProfessionalInformationValidationSteps.PROFESSIONAL_INFORMATION:
					analyticsService.trackEvent({
						category: AnalyticsCategory.REGISTRATION,
						action: "reachStep",
						params: ["PROFESSIONAL_INFORMATION"],
					});
					return (
						<RegisterContainer
							form={
								<ProfessionalInfoStep
									onSuccess={() =>
										setCurrentStep(
											userStatusManager.identityStatus.get() === IdentityStatus.NOT_NEEDED
												? ProfessionalInformationValidationSteps.DONE
												: ProfessionalInformationValidationSteps.IDENTITY_VERIFICATION,
										)
									}
								/>
							}
						>
							<RegisterDescriptionProfInfo step={ProfessionalInformationValidationSteps.PROFESSIONAL_INFORMATION} />
						</RegisterContainer>
					);
				case ProfessionalInformationValidationSteps.IDENTITY_VERIFICATION:
					analyticsService.trackEvent({
						category: AnalyticsCategory.REGISTRATION,
						action: "reachStep",
						params: ["IDENTITY_VERIFICATION"],
					});
					return (
						<RegisterContainer
							form={
								<RegisterOnfidoIdentityVerificationStep
									onComplete={() => setCurrentStep(ProfessionalInformationValidationSteps.DONE)}
								/>
							}
						>
							<RegisterDescriptionProfInfo step={ProfessionalInformationValidationSteps.IDENTITY_VERIFICATION} />
						</RegisterContainer>
					);
				case ProfessionalInformationValidationSteps.DONE:
					analyticsService.trackEvent({
						category: AnalyticsCategory.REGISTRATION,
						action: "reachStep",
						params: ["DONE"],
					});
					return <RegisterCompletedStep onFinish={() => navigate(RoutesPath.Home)}></RegisterCompletedStep>;
			}
		},
		[currentPtId, navigate],
	);

	return (
		<>
			<RegisterProgressionBar step={isScenario2 || isScenario3 ? undefined : currentStep} />
			<Container>{renderStep(currentStep)}</Container>
		</>
	);
};

const Container = styled.div`
	${stack(undefined, "center", "center")};
	flex-grow: 1;
	background-color: ${colors.white};
`;
